import { AmplifyError, ErrorMessage } from "../../types/Error";
import { IAuthClient } from "./AuthClient";
import { LoginResult, ChallengeData } from "../../types/Auth";

interface CognitoError {
    code: string;
    message: string;
}

function isCognitoError(object: unknown): object is CognitoError {
    return (object as CognitoError).code !== undefined && (object as CognitoError).message !== undefined;
}

/**
 * This is composed of an IAuthClient. The concrete implementation of this
 * interface can change, while maintaining a stable authentication API regardless of the
 * technology we use to provide the authentication details (such as Cognito).
 */
export default class AuthService {
    authClient: IAuthClient;

    constructor(client: IAuthClient) {
        this.authClient = client;
    }

    /**
     * Logs in a user and establishes a session.
     *
     * @param username - the username of the user attempting sign-in.
     * @param password - the password of the user attempting sign-in
     * @returns a Promise with either a defined Session or undefined data in the event of an error.
     */
    async login(username: string, password: string): Promise<LoginResult> {
        return new Promise<LoginResult>((resolve, reject) => {
            this.authClient
                .login(username, password)
                .then((result) => {
                    resolve(result);
                })
                .catch((error) => {
                    if (isCognitoError(error)) {
                        if (
                            error.code === AmplifyError.UserNotFoundException ||
                            error.code === AmplifyError.NotAuthorizedException
                        ) {
                            reject(new Error(ErrorMessage.InvalidLoginCredentials));
                            return;
                        }
                    }

                    reject(new Error(ErrorMessage.Default));
                });
        });
    }

    /**
     * Logout a user and clears any active session or session artifacts.
     */
    logout() {
        this.authClient.logout();
    }

    /**
     * Fetches the active user's identity token.
     *
     * @returns the identity token of the active user, if any, else undefined.
     */
    getIdentityToken(): string | null {
        const token = this.authClient.getIdentityToken()?.getJwtToken();
        if (token) return token;
        return null;
    }

    /**
     * Fetches a valid identity token, automatically refreshing if needed.
     * Refreshes the token if it expires within the 5-minute buffer window.
     *
     * @returns the identity token of the active user, if any, else null.
     */
    async getValidToken(): Promise<string | null> {
        return this.authClient.getValidToken();
    }

    /**
     * Refreshes the active user's session. If the refresh fails then the
     * user is forcibly logged out.
     *
     * @returns a Promise which may be rejected in the event of an error.
     */
    async refreshSession(): Promise<void> {
        try {
            await this.authClient.refreshSession();
        } catch {
            this.authClient.logout();
            throw new Error(ErrorMessage.Default);
        }
    }

    /**
     * Changes the password of the user.
     *
     * @param oldPassword - the old password that the user has configured.
     * @param newPassword - the new password that the user wishes to use.
     * @throws an error if the password change was rejected.
     */
    async changePassword(oldPassword: string, newPassword: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.authClient
                .changePassword(oldPassword, newPassword)
                .then(() => {
                    resolve(undefined);
                })
                .catch((error) => {
                    if (isCognitoError(error)) {
                        if (error.code === AmplifyError.NotAuthorizedException) {
                            reject(ErrorMessage.InvalidChangePasswordCredentials);
                            return;
                        }
                    }

                    reject(ErrorMessage.Default);
                });
        });
    }

    getUsername(): string | null {
        return this.authClient.getUsername();
    }

    async getUserAnnotation(): Promise<string | null> {
        return this.authClient.getUserAnnotation();
    }

    /**
     * Complete the new password required challenge.
     *
     * @param newPassword - the new password that the user wishes to set.
     * @throws an error if the password change was rejected.
     */
    async completeNewPassword(newPassword: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.authClient
                .completeNewPassword(newPassword)
                .then(() => {
                    resolve(undefined);
                })
                .catch((error) => {
                    if (isCognitoError(error)) {
                        if (error.code === AmplifyError.NotAuthorizedException) {
                            reject(new Error(ErrorMessage.InvalidChangePasswordCredentials));
                            return;
                        }
                        if (error.code === AmplifyError.InvalidPasswordException) {
                            reject(new Error(ErrorMessage.InvalidNewPassword));
                            return;
                        }
                    }

                    reject(new Error(ErrorMessage.Default));
                });
        });
    }

    /**
     * Get current challenge data if any.
     *
     * @returns the current challenge data or null if no challenge is active.
     */
    getChallengeData(): ChallengeData | null {
        return this.authClient.getChallengeData();
    }

    /**
     * Initializes the session by recovering it from localStorage if available.
     * Should be called before rendering protected content to ensure session recovery is complete.
     *
     * @returns a Promise that resolves once session recovery is complete.
     */
    async initializeSession(): Promise<void> {
        return this.authClient.initializeSession();
    }

    /**
     * Returns true if the session initialization has completed.
     */
    isSessionReady(): boolean {
        return this.authClient.isSessionReady();
    }

    /**
     * Fetches a custom attribute from the active user's Cognito attributes.
     * @param attributeName - the name of the custom attribute (e.g. "custom:video-modal")
     * @returns a Promise with the attribute value or null if not present.
     */
    async getUserCustomAttribute(attributeName: string): Promise<string | null> {
        return this.authClient.getUserCustomAttribute(attributeName);
    }

    /**
     * Initiates the forgot password flow by sending a verification code to the user's email.
     *
     * @param username - the username of the user requesting password reset.
     * @throws an error if the request fails.
     */
    async forgotPassword(username: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.authClient
                .forgotPassword(username)
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    if (isCognitoError(error)) {
                        if (error.code === AmplifyError.UserNotFoundException) {
                            reject(new Error(ErrorMessage.InvalidUsername));
                            return;
                        }
                        if (error.code === AmplifyError.LimitExceededException) {
                            reject(new Error(ErrorMessage.TooManyAttempts));
                            return;
                        }
                    }
                    reject(new Error(ErrorMessage.Default));
                });
        });
    }

    /**
     * Completes the forgot password flow by verifying the code and setting a new password.
     *
     * @param username - the username of the user.
     * @param code - the verification code sent to the user's email.
     * @param newPassword - the new password to set.
     * @throws an error if the password reset fails.
     */
    async confirmForgotPassword(username: string, code: string, newPassword: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.authClient
                .confirmForgotPassword(username, code, newPassword)
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    if (isCognitoError(error)) {
                        if (error.code === AmplifyError.CodeMismatchException) {
                            reject(new Error(ErrorMessage.InvalidVerificationCode));
                            return;
                        }
                        if (error.code === AmplifyError.ExpiredCodeException) {
                            reject(new Error(ErrorMessage.ExpiredVerificationCode));
                            return;
                        }
                        if (error.code === AmplifyError.InvalidPasswordException) {
                            reject(new Error(ErrorMessage.InvalidNewPassword));
                            return;
                        }
                    }
                    reject(new Error(ErrorMessage.Default));
                });
        });
    }
}
